import type { Reminder } from "@/data/queries";
import { dayKey } from "@/ui/format";

// One place for task vocabulary so list, detail, board and composer agree.
export const TYPES = [
  { value: 0, key: "todo", label: "To-do", icon: "check-circle-outline", tone: "tt-todo" },
  { value: 1, key: "follow", label: "Follow-up", icon: "chat-dots-outline", tone: "tt-follow" },
  { value: 2, key: "appt", label: "Appointment", icon: "clock-outline", tone: "tt-appt" },
] as const;
export const typeOf = (t: Pick<Reminder, "reminder_type">) => TYPES.find((x) => x.value === (t.reminder_type ?? 0)) ?? TYPES[0];

export const isClosed = (t: Reminder) => !!(t.completed_at || t.canceled_at);
export const addDays = (n: number) => dayKey(new Date(Date.now() + n * 86_400_000).toISOString());

// details holds "Title\n\nNotes"; the first line is the title.
export function splitDetails(details?: string | null): { title: string; notes: string } {
  const text = (details || "").trim();
  const [first, ...rest] = text.split(/\n+/);
  return { title: first || "Untitled task", notes: rest.join("\n").trim() };
}

export type Bucket = "overdue" | "today" | "week" | "later" | "done";
export function bucket(t: Reminder): Bucket {
  if (isClosed(t)) return "done";
  const today = addDays(0);
  const d = t.reminder_date || today;
  if (d < today) return "overdue";
  if (d === today) return "today";
  if (d <= addDays(7)) return "week";
  return "later";
}
export const BUCKET_LABEL: Record<Bucket, string> = { overdue: "Overdue", today: "Today", week: "This week", later: "Later", done: "Done" };

export function dueLabel(t: Reminder): string {
  if (t.completed_at) return "Done";
  if (t.canceled_at) return "Canceled";
  const d = t.reminder_date;
  if (!d) return "No date";
  const today = addDays(0);
  const time = t.reminder_time ? ` · ${fmtTime(t.reminder_time)}` : "";
  if (d === today) return `Today${time}`;
  if (d === addDays(1)) return `Tomorrow${time}`;
  if (d === addDays(-1)) return `Yesterday${time}`;
  const date = new Date(d + "T12:00:00");
  const days = Math.round((date.getTime() - Date.now()) / 86_400_000);
  const label = date.toLocaleDateString(undefined, { weekday: Math.abs(days) < 7 ? "long" : undefined, month: "short", day: "numeric" });
  return `${label}${time}`;
}

export function fmtTime(t: string) {
  const [h, m] = t.split(":").map(Number);
  const d = new Date(); d.setHours(h, m || 0, 0, 0);
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
